import { useMemo } from 'react';
import useSWR from 'swr';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { MyInfoState } from '@/recoil/atom';
import { APPROVAL_V2_RECENT_APPROVERS_URL } from '@/constant/approval';
import { getNestedMetaDataFetcher } from '@/utils/apis';
import sortApproversByOrder from '@/utils/approval/sortApproversByOrder';
import {
  type I18nText,
  type Lang,
  normalizeLang,
  pickI18nText,
} from '@/utils/approval/i18nText';
import type { Meta } from '@/types/legacyApi';
import type {
  RecentApprovalLineApprover,
  RecentApprovalLineSummary,
} from '@/components/Approval/ApprovalCreate/RecentApprovalLineList';

// v2 approvers[].type — 1=결재, 2=협조. v1과 달리 협조가 별도 coApprovers 배열이 아니라 이 값으로 섞여 온다.
// 판정 기준은 approvalDetailToFlow 와 동일 — 타입이 추가돼도 두 화면이 갈리지 않게.
const APPROVER_TYPE_APPROVAL = 1;
const APPROVER_TYPE_COOPERATION = 2;

type RecentApproverApi = {
  passportId: string;
  type?: number;
  order: number;
  status?: string;
  name: I18nText | string | null;
  departmentName?: I18nText | string | null;
  positionName?: I18nText | string | null;
  profileImageUrl?: string | null;
};

type RecentReferrerApi = {
  passportId: string;
  name: I18nText | string | null;
  departmentName?: I18nText | string | null;
  positionName?: I18nText | string | null;
  profileImageUrl?: string | null;
};

type RecentApprovalDocumentApi = {
  documentId: string;
  type?: { code: number };
  status?: string;
  subject: string;
  authoredAt?: string;
  approvers: RecentApproverApi[];
  referrers?: RecentReferrerApi[];
};

type RecentApproversResponse = {
  meta: Meta;
  data: RecentApprovalDocumentApi[];
};

type UseRecentApproversParams = {
  /** false면 SWR key=null로 호출 skip. 패널이 닫혀있을 때 호출 방지용. */
  enabled: boolean;
  /** 검색 버튼이 마지막으로 확정한 keyword. 입력 중 keyword와는 분리. 빈 문자열도 유효. */
  keyword: string;
  /** 현재 작성 중인 품의 양식 코드 — 동일 양식 문서만 조회 (PORTAL-13143). 미전달 시 전체 양식. */
  type?: number;
  /** true면 완료(C) 문서만 */
  completed?: boolean;
  /** 페이지당 카드 수 (default 3) */
  listSize?: number;
  /** 1-based 현재 페이지 */
  currentPage: number;
};

type BuildUrlParams = Pick<
  UseRecentApproversParams,
  'keyword' | 'type' | 'completed' | 'currentPage'
> & { listSize: number };

const buildUrl = ({
  keyword,
  type,
  completed,
  listSize,
  currentPage,
}: BuildUrlParams) => {
  const params = new URLSearchParams({
    listSize: String(listSize),
    currentPage: String(currentPage),
  });
  if (type != null) params.set('type', String(type));
  if (completed != null) params.set('completed', String(completed));
  // v2는 keyword 미지원(2026-08-04 실호출 확인 — 전달해도 무시되고 전체 반환).
  // 백엔드 지원 결정 대기 중이라 전달만 유지 — 지원되는 순간 그대로 동작.
  if (keyword) params.set('keyword', keyword);
  return `${APPROVAL_V2_RECENT_APPROVERS_URL}?${params.toString()}`;
};

const toPerson = (
  p: RecentApproverApi | RecentReferrerApi,
  lang: Lang,
): RecentApprovalLineApprover => ({
  passportId: p.passportId,
  name: pickI18nText(p.name, lang),
  positionName: pickI18nText(p.positionName, lang),
  departmentName: pickI18nText(p.departmentName, lang),
  profileImageUrl: p.profileImageUrl ?? null,
});

/**
 * approvers를 type으로 결재/협조로 나눠 order 정렬 후 카드 표시 시퀀스 구성.
 * - 기안자(본인) 행 제외: 내가 상신한 문서라 approvers 에 본인이 포함될 수 있음.
 *   order/status 패턴은 문서에 따라 달라 믿을 수 없으므로 passportId 일치로만 판정.
 *   cooperationInsertIndex 는 제외 후 배열 기준으로 계산 (협조 블록 위치가 밀리지 않게).
 * - cooperationInsertIndex: 첫 협조자.order보다 작은 order 를 가진 결재자 개수
 *   → 카드/디테일이 결재 chip 사이에 협조 블록을 그 위치에 끼워 넣음.
 *   (협조 블록은 한 덩어리 contiguous 가정. 분산 케이스는 현재 모델 미지원)
 * - cooperationOrdered: 협조자들의 order가 모두 동일하면 false(병렬),
 *   서로 다르면 true(순차) — 디테일에서 chevron 노출 결정.
 */
const buildSummary = (
  doc: RecentApprovalDocumentApi,
  myPassportId: string,
  lang: Lang,
): RecentApprovalLineSummary => {
  const approvers = sortApproversByOrder(
    (doc.approvers ?? [])
      .filter((a) => a.type === APPROVER_TYPE_APPROVAL)
      .filter((a) => a.passportId !== myPassportId),
  );
  const coApprovers = sortApproversByOrder(
    (doc.approvers ?? []).filter((a) => a.type === APPROVER_TYPE_COOPERATION),
  );

  let cooperationInsertIndex: number | undefined;
  if (coApprovers.length > 0) {
    const firstCoopOrder = coApprovers[0].order;
    cooperationInsertIndex = approvers.filter(
      (a) => a.order < firstCoopOrder,
    ).length;
  }

  const coopOrders = coApprovers.map((c) => c.order);
  const cooperationOrdered =
    coopOrders.length > 1 && new Set(coopOrders).size > 1;

  return {
    id: doc.documentId,
    title: doc.subject,
    approvers: approvers.map((a) => toPerson(a, lang)),
    cooperators:
      coApprovers.length > 0
        ? coApprovers.map((c) => toPerson(c, lang))
        : undefined,
    cooperationInsertIndex,
    cooperationOrdered,
    referrers: doc.referrers?.length
      ? doc.referrers.map((r) => toPerson(r, lang))
      : undefined,
    lastUsedAt: doc.authoredAt ?? '',
  };
};

/**
 * 결재라인 패널의 '최근 결재라인' 조회 — GET /api/v2/approval/recent-approvers.
 * - type: 작성 중 양식 코드로 필터 — 다른 양식의 결재라인이 섞여 나오지 않게 (PORTAL-13143).
 * - keyword는 검색 확정(버튼/Enter) 시점 값. 입력 중 keyword와 분리해 호출 시점을 제어.
 * - keyword 빈 문자열도 유효 호출(최근 N개) — 패널 표시 시 자동 조회가 이 경로.
 * - keepPreviousData=true: 페이지 전환/재검색 중 깜빡임 방지.
 */
const useRecentApprovers = ({
  enabled,
  keyword,
  type,
  completed,
  listSize = 3,
  currentPage,
}: UseRecentApproversParams) => {
  const key = enabled
    ? buildUrl({ keyword, type, completed, listSize, currentPage })
    : null;
  const { passportId: myPassportId } = useAtomValue(MyInfoState);
  const { i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);

  const { data, error, isLoading, isValidating, mutate } =
    useSWR<RecentApproversResponse>(key, getNestedMetaDataFetcher, {
      revalidateOnFocus: false,
      keepPreviousData: true,
      // SWR 기본값은 재시도 무한(404 포함) - 3회로 제한
      errorRetryCount: 3,
    });

  const items = useMemo<RecentApprovalLineSummary[]>(
    () =>
      (data?.data ?? []).map((doc) => buildSummary(doc, myPassportId, lang)),
    [data, myPassportId, lang],
  );

  return {
    items,
    meta: data?.meta,
    isLoading,
    isValidating,
    error,
    refetch: mutate,
  };
};

export default useRecentApprovers;
